import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";

import { getDeal, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { DealCreationBlocked } from "@/components/deals/DealCreationBlocked";
import { DealRegistrationWizard } from "@/components/deals/DealRegistrationWizard";
import { InlineAlert } from "@/components/ui/inline-alert";
import { useTrainingLockout } from "@/lib/useTrainingLockout";

/**
 * CHP-804: a dismissible "couldn't load that draft" banner shown above a fresh
 * wizard when a ?draftId fetch fails (invalid/expired id), so a user who
 * clicked "Resume" gets a signal instead of a silent blank wizard.
 */
function DraftLoadError({ onDismiss }: { onDismiss: () => void }) {
  return (
    <InlineAlert tone="warning" className="mb-4">
      <span className="flex items-center justify-between gap-3" data-testid="draft-load-error">
        <span>Couldn&apos;t load that draft — starting fresh.</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-0.5 hover:bg-black/5"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </span>
    </InlineAlert>
  );
}

export function PartnerDealsNewRoute() {
  const { accessToken, data: user } = useCurrentUser();
  const navigate = useNavigate();
  // Resume flow: ?draftId=<uuid> to pick up where the user left off.
  const search = useSearch({ strict: false }) as { draftId?: string };
  const draftId = search.draftId;

  // RACCPE-22: a BDM with outstanding compulsory training can't create OR
  // submit a deal (both endpoints are gated server-side), so the whole wizard
  // — fresh start, deep-link, and draft resume — is replaced with the block.
  const lockout = useTrainingLockout();

  const draft = useQuery<Deal>({
    queryKey: ["deals", "detail", draftId, accessToken],
    queryFn: () => getDeal(accessToken!, draftId!),
    enabled: !!accessToken && !!draftId,
  });
  const [draftErrorDismissed, setDraftErrorDismissed] = useState(false);

  if (lockout.blocked) {
    return <DealCreationBlocked statuses={lockout.statuses} draftId={draftId} />;
  }

  // While fetching an existing draft, show a loading state.
  if (draftId && draft.isLoading) {
    return (
      <p role="status" className="container py-6">
        Loading draft...
      </p>
    );
  }

  // CHP-458: a CM/RA/RM can reach this partner registration route — notably a
  // ResetData Admin, who lands on /dashboard/partner (RA has no Admin Hub,
  // RACCPE-17) and so clicks a partner-framed "Register a Deal" CTA. Per
  // CHP-202/368 a non-partner must register ON BEHALF via the Assign-Deal-Owner
  // first step, not as themselves: without it the draft create POSTs no
  // target_bdm_id and the backend 422s ("target_bdm_id required when creating on
  // behalf"). Drive the wizard in on-behalf mode for any non-partner — the same
  // manager-vs-partner split DealConfirmation already uses (role-based, not
  // route-based, so every entry point is covered). ProtectedRoute resolves the
  // user before this renders, so `user` is defined; the `!!user` guard is
  // belt-and-braces.
  const onBehalf = !!user && user.role !== "channel_partner";

  // If the draftId was invalid or the fetch failed, fall through to a fresh
  // wizard (the user can start over) — but signal the failure first (CHP-804).
  const showDraftError = !!draftId && draft.isError && !draftErrorDismissed;
  return (
    <>
      {showDraftError ? (
        <div className="container pt-6">
          <DraftLoadError onDismiss={() => setDraftErrorDismissed(true)} />
        </div>
      ) : null}
      <DealRegistrationWizard
        initialDeal={draft.data}
        onBehalf={onBehalf}
        // CHP-1014: Step-1 Cancel — after the discard confirm, return to the
        // deals dashboard (any saved draft stays resumable).
        onCancel={() => navigate({ to: "/dashboard/partner/deals" })}
        // CHP-1033: wizard-wide header link back to the same dashboard.
        backTo="/dashboard/partner/deals"
      />
    </>
  );
}
